import React, { useState } from 'react';
import InfoSVG from '../svg/InfoSVG';
import Popover from 'reactstrap/lib/Popover';
import PopoverBody from 'reactstrap/lib/PopoverBody';
import { EntityVersion, EntityVersionType } from 'types';
import EditStatusLabel from './EditStatusLabel';
import { formatDateTime } from 'util/Time';
import CheckCircleSVG from '../svg/CheckCircleSVG';
import DashCircleSVG from '../svg/DashCircleSVG';
import FolderSVG from '../svg/FolderSVG';
import CalendarSVG from '../svg/CalendarSVG';
import CalendarPowerSVG from '../svg/CalendarPowerSVG';
import CalendarCheckmarkSVG from '../svg/CalendarCheckmarkSVG';
import CalendarTrashSVG from '../svg/CalendarTrashSVG';
import GarbageCanCircleSVG from '../svg/GarbageCanCircleSVG';
import PencilWriteSVG from '../svg/PencilWriteSVG';
import { ModelStatus } from '../../../../../Roc.SharedClientApp/types/model-status';

interface EditVersionStatusProps {
	version: EntityVersion;
	index: number;
}

interface EditVersionListingStatusIconProps {
	version: EntityVersion;
	index: number;
}

/**
 * Component for rendering version listing status icons on the Versions tab on Admin Edit Page.
 *
 * @param {EditVersionListingStatusIconProps} props
 */
function EditVersionListingStatusIcon(props: EditVersionListingStatusIconProps) {
	const { type, status } = props.version;
	const defaultIconStyles = 'tabs-table__td-icon';
	const archivedIconStyles = 'listing-item-status-icon__right';

	switch (type) {
		case EntityVersionType.Current:
			if (status === ModelStatus.Published) {
				return <CheckCircleSVG class={defaultIconStyles} testId={'icon-check-' + props.index} />;
			} else if (status === ModelStatus.Inactive) {
				return <DashCircleSVG class={defaultIconStyles} testId={'icon-dash-' + props.index} />;
			} else if (status === ModelStatus.Deleted) {
				return <GarbageCanCircleSVG class={defaultIconStyles} testId={'icon-garbage-can-' + props.index} />;
			} else {
				return <InfoSVG class={defaultIconStyles} testId={'icon-info-' + props.index} />;
			}
		case EntityVersionType.Future:
			if (status === ModelStatus.Published) {
				return (
					<CalendarCheckmarkSVG
						class={defaultIconStyles + ' icon-calendar-checkmark'}
						testId={'icon-calendar-checkmark-' + props.index}
					/>
				);
			} else if (status === ModelStatus.Inactive) {
				return (
					<CalendarPowerSVG
						class={defaultIconStyles + ' icon-calendar-power'}
						testId={'icon-calendar-power-' + props.index}
					/>
				);
			} else if (status === ModelStatus.Deleted) {
				return (
					<CalendarTrashSVG
						class={defaultIconStyles + ' icon-calendar-trash'}
						testId={'icon-calendar-trash-' + props.index}
					/>
				);
			} else {
				return <CalendarSVG class={defaultIconStyles} testId={'icon-calendar-' + props.index} />;
			}
		case EntityVersionType.Archived:
			if (status === ModelStatus.Published) {
				return <CheckCircleSVG class={archivedIconStyles} testId={'icon-check-' + props.index} />;
			} else if (status === ModelStatus.Inactive) {
				return <DashCircleSVG class={archivedIconStyles} testId={'icon-dash-' + props.index} />;
			} else if (status === ModelStatus.Deleted) {
				return <GarbageCanCircleSVG class={archivedIconStyles} testId={'icon-garbage-can-' + props.index} />;
			} else {
				return <InfoSVG class={archivedIconStyles} testId={'icon-info-' + props.index} />;
			}
		case EntityVersionType.Draft:
			return (
				<PencilWriteSVG class={defaultIconStyles + ' icon-pencil-write'} testId={'icon-draft-' + props.index} />
			);
		default:
			return <InfoSVG class={defaultIconStyles} testId={'icon-info-' + props.index} />;
	}
}

// Gets status text for icon popover
function getVersionStatus(version: EntityVersion) {
	const { type, status } = version;

	switch (type) {
		case EntityVersionType.Current:
			return status || 'Current';
		case EntityVersionType.Archived:
		case EntityVersionType.Future:
			if (status === 'Published') {
				return `${EntityVersionType[type]} Publish`;
			} else if (status === 'Inactive') {
				return `${EntityVersionType[type]} Inactivation`;
			} else if (status === 'Deleted') {
				return `${EntityVersionType[type]} Deletion`;
			} else {
				return 'Archived';
			}
		case EntityVersionType.Draft:
			return 'Draft';
		default:
			return 'Unknown';
	}
}

/**
 * Component for rendering the status popup for version entities.
 *
 * @param {EditVersionStatusProps} props
 * @returns
 */
function EditVersionStatus(props: EditVersionStatusProps) {
	const [isStatusPopoverOpen, setIsStatusPopoverOpen] = useState(false);

	const {
		version: { id },
		version,
		index,
	} = props;

	/**
	 * Toggle value for isStatusPopoverOpen state.
	 *
	 */
	function toggleStatusPopover() {
		setIsStatusPopoverOpen(!isStatusPopoverOpen);
	}

	return (
		<div className="listing-item-status-icon-wrapper">
			{version.type === EntityVersionType.Archived ? (
				<div>
					<FolderSVG class={'listing-item-status-icon__left'} testId={`icon-archived-${index}`} />
				</div>
			) : null}
			<div>
				<button
					className="listing-status-btn px-2"
					id={id ? 'Popover-' + id : 'Popover-current'}
					type="button"
					onClick={toggleStatusPopover}
				>
					<EditVersionListingStatusIcon version={version} index={index} />
				</button>
				<Popover
					target={id ? 'Popover-' + id : 'Popover-current'}
					placement="right"
					isOpen={isStatusPopoverOpen}
					toggle={toggleStatusPopover}
					className="popover-wider"
				>
					<PopoverBody>
						<EditStatusLabel label={'Status'} value={getVersionStatus(version)} />
						<EditStatusLabel label={'Version Number'} value={version.versionNo} />
						<EditStatusLabel
							label={
								(version.type === EntityVersionType.Future ||
								version.type === EntityVersionType.Archived
									? 'Originally '
									: '') + 'Created By'
							}
							value={version.createUser}
						/>
						<EditStatusLabel
							label={
								(version.type === EntityVersionType.Future ||
								version.type === EntityVersionType.Archived
									? 'Originally '
									: '') + 'Created On'
							}
							value={formatDateTime(version.createDate)}
						/>
						<EditStatusLabel
							label={
								(version.type == EntityVersionType.Future
									? 'Scheduled'
									: version.type == EntityVersionType.Archived
									? 'Archived'
									: 'Modified') + ' By'
							}
							value={version.modifyUser}
						/>
						<EditStatusLabel
							label={
								version.type === EntityVersionType.Future
									? 'Scheduled For'
									: version.type == EntityVersionType.Archived
									? 'Archived On'
									: 'Modified On'
							}
							value={
								version.type === EntityVersionType.Future && version.scheduledTime
									? formatDateTime(version.scheduledTime)
									: formatDateTime(version.modifyDate)
							}
						/>
					</PopoverBody>
				</Popover>
			</div>
		</div>
	);
}

export default EditVersionStatus;
